import { useEffect, useState } from "react";
import { BaseLoading } from "../../../library";
import i18n from "../../../language/i18n";
import { generateDiagramFromDescription } from "../../../requests/AIRequests";
import {
  AI_POOL_FREED_EVENT,
  AI_REFUSAL_RESET_EVENT,
  dispatchAiRefusal,
} from "../../../utilization/AiRefusalUtilization";

/**
 * Renders an inline diagram the AI tutor requested via a `[DIAGRAM: <description>]` marker.
 * The backend writes the TikZ and compiles it to a PNG; this component posts the
 * description, shows a "drawing…" placeholder meanwhile, then swaps in the image — or a small
 * muted "unavailable" note on failure. Only ever mounted for a COMPLETE marker (see ChatBubble),
 * so `description` is stable and the render fires once.
 *
 * A module-level cache dedups the request across re-renders, the streaming→persist transition,
 * and remounts, keyed by the description. A failed render is NOT cached (so it retries), and the
 * cache is loosely bounded to avoid unbounded growth over a long session.
 */

const MAX_CACHE = 60;
const diagramCache = new Map<string, Promise<DiagramOutcome>>();

// When the AI budget frees, the cached refusals stop being true — so they are dropped and the next
// render of those markers asks again. Only the refusals: a successfully drawn diagram stays cached.
//
// Both events are listened for, and they mean different things. `aiPoolFreed` is the one that matters
// here: it fires when a credit read names no blocked window, i.e. the budget is genuinely back. The
// session reset is kept alongside it because a cached refusal must not outlive the user who earned it
// either. Listening ONLY for the reset — as this first shipped — meant nothing ever announced a freed
// pool, so a diagram refused once stayed refused until the page was reloaded.
const dropCachedRefusals = () => {
  diagramCache.forEach((pending, key) => {
    pending
      .then((outcome) => {
        if (outcome.limitReached) diagramCache.delete(key);
      })
      .catch(() => diagramCache.delete(key));
  });
};

if (typeof window !== "undefined") {
  window.addEventListener(AI_POOL_FREED_EVENT, dropCachedRefusals);
  window.addEventListener(AI_REFUSAL_RESET_EVENT, dropCachedRefusals);
}

// The backend contract is a raw base64 PNG (no data-URI prefix). Always wrap it as PNG rather
// than trusting a backend-supplied `data:` prefix verbatim — that avoids content-type confusion
// (e.g. a `data:image/svg+xml;…`) being placed into <img src>, at zero cost to the real path.
const toDataUri = (base64Image: string): string => `data:image/png;base64,${base64Image}`;

/**
 * What the diagram route said, distinct from "no image".
 *
 * This endpoint answers HTTP 200 with `{ success: false, error }` for every failure — deliberately, so a
 * missing picture never breaks the tutor's reply. The cost is that the axios interceptor cannot see its
 * refusals at all, so a spent AI budget looked identical to an unrenderable diagram and read as
 * "diyagram çizilemedi". Both halves are fixed here: the note says which it was, and the refusal is
 * announced so the app-wide band can name the reset time.
 */
export type DiagramOutcome = { uri: string | null; limitReached: boolean };

const resolveDiagram = (description: string): Promise<DiagramOutcome> => {
  const key = description;
  const existing = diagramCache.get(key);
  if (existing) {
    // Refresh recency (LRU): re-insert so a still-referenced diagram isn't the next FIFO eviction.
    diagramCache.delete(key);
    diagramCache.set(key, existing);
    return existing;
  }

  const promise = generateDiagramFromDescription({ description })
    .then((response: any) => {
      if (response.err) return { uri: null, limitReached: false };
      const data = response.data;
      if (!data || !data.success || !data.base64Image) {
        // `errorMessage`, not `error`: that is the field DiagramFromDescriptionResponse actually
        // carries (success / base64Image / errorMessage). Reading the wrong name silently made
        // every branch below unreachable — the refusal was never announced and the learner kept
        // reading the generic "diyagram çizilemedi".
        const limitReached = data?.errorMessage === "tokenBudgetExceeded";
        if (limitReached) {
          // Announced by hand because this refusal never reaches the interceptor: it arrives as a 200.
          dispatchAiRefusal({
            kind: "limit",
            errorKey: "tokenBudgetExceeded",
            url: null,
          });
        }
        return { uri: null, limitReached };
      }
      return { uri: toDataUri(data.base64Image), limitReached: false };
    })
    .catch(() => ({ uri: null, limitReached: false }))
    .then((outcome) => {
      // Do not let a failed render stick for the session — drop it so a retry can happen.
      //
      // A BUDGET refusal is the exception: re-POSTing a marker the service has just refused cannot
      // succeed until the pool frees, and a learner scrolling a long reply would re-ask for every
      // diagram in it. It is kept, and dropped when the pool actually comes back (below).
      if (outcome.uri === null && !outcome.limitReached) diagramCache.delete(key);
      return outcome;
    });

  if (diagramCache.size >= MAX_CACHE) {
    const oldestKey = diagramCache.keys().next().value;
    if (oldestKey !== undefined) diagramCache.delete(oldestKey);
  }
  diagramCache.set(key, promise);
  return promise;
};

interface AiDiagramProps {
  description: string;
  // True while the marker is still streaming in (closing "]" not yet arrived): show the
  // placeholder but do NOT fire a request until the description is final.
  pending?: boolean;
}

const AiDiagram = ({ description, pending = false }: AiDiagramProps) => {
  const [status, setStatus] = useState<"loading" | "done" | "error" | "limit">(
    "loading",
  );
  const [dataUri, setDataUri] = useState<string | null>(null);

  useEffect(() => {
    if (pending) {
      // Still streaming — show the placeholder, don't fire a request yet.
      setStatus("loading");
      setDataUri(null);
      return;
    }
    if (!description) {
      // Complete but empty (defensive — splitDiagramSegments already drops empty complete
      // markers): nothing to draw, so surface the "unavailable" note instead of a stuck spinner.
      setStatus("error");
      setDataUri(null);
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setDataUri(null);
    resolveDiagram(description).then(({ uri, limitReached }) => {
      if (cancelled) return;
      if (uri) {
        setDataUri(uri);
        setStatus("done");
        return;
      }
      setStatus(limitReached ? "limit" : "error");
    });
    return () => {
      cancelled = true;
    };
  }, [description, pending]);

  if (status === "done" && dataUri) {
    return (
      <span className="dgm-wrap">
        <img className="dgm-img" src={dataUri} alt={i18n.t("diagram")} />
      </span>
    );
  }

  if (status === "limit" || status === "error") {
    return (
      <span className="dgm-wrap">
        <span className="dgm-frame dgm-text">
          {i18n.t(
            status === "limit" ? "diagram_unavailable_limit" : "diagram_unavailable",
          )}
        </span>
      </span>
    );
  }

  return (
    <span className="dgm-wrap">
      <span className="dgm-frame">
        <BaseLoading size="small" primaryColor="#57C2D3" secondaryColor="#57C2D3" />
        <span className="dgm-text">{i18n.t("drawing_diagram")}</span>
      </span>
    </span>
  );
};

export default AiDiagram;
